/**
 * Admin AI Usage Page
 *
 * What users generated, and what it cost us:
 * - Aggregate usage and provider cost, this month and since metering started
 * - Anything that makes the cost figure unreliable, stated beside it
 * - Margin: what the month's subscriptions bring in against its whole cost
 * - Tier breakdown
 * - Top users, ranked by generations or by cost, and the accounts below cost
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Cloud,
  Cpu,
  CurrencyDollar,
  Receipt,
  Scales,
  TrendDown,
  TrendUp as TrendingUp,
  Users,
  Warning,
} from "@phosphor-icons/react";
import { Spinner } from "@/components/ui/Spinner";
import { hasRole } from "@/components/auth/AdminGuard";
import { useAdminAIUsage, useAdminAIUsageDaily } from "@/hooks/useAdmin";
import {
  type AIUsageDailyWindow,
  type AIUsageTopUser,
  COST_NOT_REPORTED,
  type CostSummary,
  type FullyLoadedCost,
  type PlatformCost,
  type PriceChangeReport,
} from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";
import { formatMicroUsd, formatPeriod } from "@/utils/formatting";

/** A `YYYY-MM-DD` day, as a short date in UTC. */
function formatDay(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? day
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(parsed);
}

const TIER_COLORS: Record<string, string> = {
  free: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  pro: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  team: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  enterprise:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

function StatCard({
  label,
  value,
  detail,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  detail?: string;
  icon: React.ElementType;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-card p-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold tabular-nums">
          {typeof value === "number" ? value.toLocaleString() : value}
        </p>
        <p className="text-xs text-muted-foreground">{label}</p>
        {detail && (
          <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
            {detail}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Says when the cost total is wrong, and in which direction, instead of
 * showing a figure that looks exact.
 */
function CostReliabilityNotice({ cost }: { cost: CostSummary }) {
  const problems: string[] = [];
  if (cost.unpricedCalls > 0) {
    problems.push(
      `${cost.unpricedCalls.toLocaleString()} ${cost.unpricedCalls === 1 ? "call has" : "calls have"} no published price and ${cost.unpricedCalls === 1 ? "was" : "were"} recorded at $0, so the total is understated.`
    );
  }
  if (cost.fallbackCalls > 0) {
    problems.push(
      `${cost.fallbackCalls.toLocaleString()} ${cost.fallbackCalls === 1 ? "call" : "calls"} used a model with no listed rate and ${cost.fallbackCalls === 1 ? "was" : "were"} priced at the most expensive one, so the total may be overstated.`
    );
  }
  if (problems.length === 0) return null;

  return (
    <div
      role="status"
      className="flex gap-3 rounded-xl border border-amber-300/60 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-600/30 dark:bg-amber-900/10 dark:text-amber-200"
    >
      <Warning className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="space-y-1">
        <p className="font-medium">This month&apos;s cost is not exact.</p>
        {problems.map((problem) => (
          <p key={problem}>{problem}</p>
        ))}
        <p className="text-xs opacity-80">
          Add the missing rates to the billing config to fix this.
        </p>
      </div>
    </div>
  );
}

/** Warn this far ahead, matching the daily cost job. */
const PRICE_CHANGE_WARNING_DAYS = 90;

function formatPercent(ratio: number | null): string {
  if (ratio === null) return "n/a";
  const sign = ratio >= 0 ? "+" : "";
  return `${sign}${(ratio * 100).toFixed(0)}%`;
}

/** A margin in the danger colour when it is a loss. */
function MarginFigure({
  microUsd,
  className,
}: {
  /** Null when the price is unknown; undefined from an API without margin. */
  microUsd: number | null | undefined;
  className?: string;
}) {
  if (microUsd === null || microUsd === undefined) {
    return (
      <span
        className={cn("text-muted-foreground", className)}
        title="No known price"
      >
        —
      </span>
    );
  }
  return (
    <span className={cn(microUsd < 0 && "text-destructive", className)}>
      {formatMicroUsd(microUsd)}
    </span>
  );
}

/** Where a user's price came from, for the tables. */
function priceLabel(user: AIUsageTopUser): string {
  if (user.priceMicroUsd === null) return "No list price";
  if (user.priceMicroUsd === undefined) return "";
  return user.priceSource === "override"
    ? `${formatMicroUsd(user.priceMicroUsd)} custom`
    : formatMicroUsd(user.priceMicroUsd);
}

/**
 * What the month's subscriptions bring in against what the month cost.
 *
 * Revenue is every user's price, list or custom, whether or not they were
 * active: they are charged regardless. Cost is the whole period — every
 * provider call, attributed or not, plus the platform bill — so the margin
 * here is smaller than the sum of the per-user ones.
 */
function MarginCard({
  month,
  revenueMicroUsd,
  costMicroUsd,
  marginMicroUsd,
  marginRatio,
  unpricedUsers,
  platformRecorded,
}: {
  month: string;
  revenueMicroUsd: number;
  costMicroUsd: number;
  marginMicroUsd: number | null;
  marginRatio: number | null;
  unpricedUsers: number;
  platformRecorded: boolean;
}) {
  const loss = marginMicroUsd !== null && marginMicroUsd < 0;
  return (
    <section
      aria-label="Margin"
      className={cn(
        "space-y-3 rounded-xl border p-5",
        loss
          ? "border-destructive/40 bg-destructive/5"
          : "border-border/50 bg-card"
      )}
    >
      <div className="flex items-center gap-2">
        <Scales className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Margin ({month})
        </h2>
      </div>
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted-foreground">Revenue</dt>
          <dd className="text-xl font-bold tabular-nums">
            {formatMicroUsd(revenueMicroUsd)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Cost</dt>
          <dd className="text-xl font-bold tabular-nums">
            {formatMicroUsd(costMicroUsd)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Margin</dt>
          <dd className="text-xl font-bold tabular-nums">
            <MarginFigure microUsd={marginMicroUsd} />
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Margin ratio</dt>
          <dd className="text-xl font-bold tabular-nums">
            {marginRatio === null
              ? "n/a"
              : `${(marginRatio * 100).toFixed(0)}%`}
          </dd>
        </div>
      </dl>
      <p className="text-xs text-muted-foreground">
        Revenue is each user&apos;s monthly price — the plan&apos;s list price,
        or the custom price set on their account — counted whether or not they
        did anything. Cost is provider calls plus AWS
        {platformRecorded ? "" : " (not recorded yet, so counted as $0)"}.
        Nothing is collected: there is no payment provider, so this is what
        accounts are charged, not what was paid.
        {unpricedUsers > 0 &&
          ` ${unpricedUsers.toLocaleString()} ${unpricedUsers === 1 ? "user has" : "users have"} no known price (Enterprise with no custom price set) and ${unpricedUsers === 1 ? "is" : "are"} not in the revenue figure.`}
      </p>
    </section>
  );
}

/** The accounts that cost more than they pay, biggest loss first. */
function BelowCostList({ users }: { users: AIUsageTopUser[] }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card p-5">
      <div className="mb-4 flex items-center gap-2">
        <TrendDown
          className="h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Below cost
        </h2>
      </div>
      {users.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">
          No account has cost more than its price this month.
        </p>
      ) : (
        <ul className="divide-y divide-border/50 text-sm">
          {users.map((user) => (
            <li
              key={user.userId}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5"
            >
              <div className="flex min-w-0 items-center gap-3">
                <UserCell user={user} />
                <span
                  className={cn(
                    "inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                    TIER_COLORS[user.tier] ?? TIER_COLORS.free
                  )}
                >
                  {user.tier}
                </span>
              </div>
              <div className="text-right tabular-nums">
                <MarginFigure
                  microUsd={user.marginMicroUsd}
                  className="font-medium"
                />
                <p className="text-xs text-muted-foreground">
                  {priceLabel(user)} against{" "}
                  {formatMicroUsd(user.fullyLoadedMicroUsd)} fully loaded
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Fully-loaded cost against the account&apos;s monthly price. Accounts
        with no known price are not listed: their margin is unknown, not
        negative.
      </p>
    </div>
  );
}

function formatRate(rate: {
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
}): string {
  return `$${rate.inputUsdPerMillion} in / $${rate.outputUsdPerMillion} out per M tokens`;
}

/**
 * Published provider price changes still ahead, at this month's volume.
 *
 * A 2x step on the migration target was previously discoverable only by
 * reading a comment in the rate card. Shown whenever a change is pending,
 * and coloured as a warning inside the window the daily job also warns in.
 */
function PriceChangeNotice({ report }: { report: PriceChangeReport }) {
  if (report.changes.length === 0) return null;
  const soon = report.changes.some(
    (c) => c.daysAway <= PRICE_CHANGE_WARNING_DAYS
  );

  return (
    <section
      aria-label="Published price changes"
      className={cn(
        "space-y-3 rounded-xl border p-5",
        soon
          ? "border-amber-300/60 bg-amber-50 dark:border-amber-600/30 dark:bg-amber-900/10"
          : "border-border/50 bg-card"
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Published price changes
        </h2>
        <p className="text-xs tabular-nums text-muted-foreground">
          At {formatPeriod(report.period)}&apos;s volume:{" "}
          {formatMicroUsd(report.totalMicroUsd)} →{" "}
          {formatMicroUsd(report.projectedMicroUsd)} (
          {formatPercent(report.deltaRatio)})
        </p>
      </div>
      <ul className="space-y-2 text-sm">
        {report.changes.map((change) => (
          <li
            key={`${change.model}-${change.from}`}
            className="flex flex-col gap-0.5"
          >
            <span>
              <span className="font-medium">{change.model}</span> on{" "}
              {formatDay(change.from)}
              <span className="text-muted-foreground">
                {" "}
                · {change.daysAway} days away
              </span>
            </span>
            <span className="text-xs text-muted-foreground">
              {formatRate(change.current)} → {formatRate(change.next)}.{" "}
              {change.currentMicroUsd > 0
                ? `This month's ${formatMicroUsd(change.currentMicroUsd)} on it would be ${formatMicroUsd(change.projectedMicroUsd)} (${formatPercent(change.deltaRatio)}).`
                : "No volume on this model this month."}
            </span>
          </li>
        ))}
      </ul>
      {report.unpricedModels.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Volume on {report.unpricedModels.join(", ")} matches nothing in the
          rate card and is not projected.
        </p>
      )}
    </section>
  );
}

const RANKINGS = [
  { key: "generations", label: "By generations" },
  { key: "cost", label: "By cost" },
] as const;

type Ranking = (typeof RANKINGS)[number]["key"];

function UserCell({ user }: { user: AIUsageTopUser }) {
  // Analysts receive neither name nor email.
  const primary = user.name || user.email;
  const content = primary ? (
    <>
      <p className="font-medium">{primary}</p>
      {user.name && user.email && (
        <p className="text-xs text-muted-foreground">{user.email}</p>
      )}
    </>
  ) : (
    <p className="font-mono text-xs text-muted-foreground">{user.userId}</p>
  );

  if (!hasRole("platform_admin", "platform_support")) return content;
  return (
    <Link
      to={`/users/${user.userId}`}
      className="block hover:text-primary hover:underline"
    >
      {content}
    </Link>
  );
}

function TopUsersTable({ users }: { users: AIUsageTopUser[] }) {
  if (users.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        No usage recorded this month yet.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">#</th>
            <th className="px-3 py-2">User</th>
            <th className="px-3 py-2">Tier</th>
            <th className="px-3 py-2 text-right">Generations</th>
            <th className="px-3 py-2 text-right">Credits</th>
            <th className="px-3 py-2 text-right">AI cost</th>
            <th className="px-3 py-2 text-right">X cost</th>
            <th className="px-3 py-2 text-right">Total cost</th>
            <th className="px-3 py-2 text-right">Fully loaded</th>
            <th className="px-3 py-2 text-right">Margin</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {users.map((user, i) => (
            <tr key={user.userId}>
              <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                {i + 1}
              </td>
              <td className="px-3 py-2.5">
                <UserCell user={user} />
              </td>
              <td className="px-3 py-2.5">
                <span
                  className={cn(
                    "inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                    TIER_COLORS[user.tier] ?? TIER_COLORS.free
                  )}
                >
                  {user.tier}
                </span>
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums">
                {user.generationsThisMonth.toLocaleString()}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                {user.creditsThisMonth !== undefined
                  ? user.creditsThisMonth.toLocaleString()
                  : "—"}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                {formatMicroUsd(user.aiCostMicroUsd)}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                {formatMicroUsd(user.xCostMicroUsd)}
              </td>
              <td className="px-3 py-2.5 text-right font-medium tabular-nums">
                {formatMicroUsd(user.costThisMonthMicroUsd)}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                {formatMicroUsd(user.fullyLoadedMicroUsd)}
              </td>
              <td
                className="px-3 py-2.5 text-right tabular-nums"
                title={priceLabel(user)}
              >
                <MarginFigure microUsd={user.marginMicroUsd} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const DAILY_WINDOWS: AIUsageDailyWindow[] = [7, 30, 90];

const DAILY_METRICS = [
  { key: "generations", label: "Generations" },
  { key: "credits", label: "Credits" },
  { key: "costMicroUsd", label: "Cost" },
] as const;
type DailyMetricKey = (typeof DAILY_METRICS)[number]["key"];

function formatDailyValue(value: number, metric: DailyMetricKey): string {
  return metric === "costMicroUsd"
    ? formatMicroUsd(value)
    : value.toLocaleString();
}

/**
 * Daily generations/credits/cost, with a 7/30/90-day selector.
 *
 * Days before the series' `since` were never instrumented — they carry no
 * data, not a real zero — so they render as muted bars with no numeric
 * tooltip, and the table below spells out "No data" rather than "0" for the
 * same days. Treating them the same as a zero-activity day would read as
 * "nothing happened" when the truth is "nobody was counting".
 */
function DailyUsageChart() {
  const [days, setDays] = useState<AIUsageDailyWindow>(7);
  const [metric, setMetric] = useState<DailyMetricKey>("generations");
  const { data, isLoading, error } = useAdminAIUsageDaily(days);

  return (
    <div className="rounded-xl border border-border/50 bg-card p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Daily Usage
        </h2>
        <div className="flex flex-wrap gap-2">
          <div
            role="group"
            aria-label="Daily metric"
            className="flex rounded-lg border border-border bg-muted/30 p-0.5 text-xs"
          >
            {DAILY_METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                aria-pressed={metric === m.key}
                onClick={() => setMetric(m.key)}
                className={cn(
                  "rounded-md px-2.5 py-1 font-medium transition-colors",
                  metric === m.key
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div
            role="group"
            aria-label="Daily window"
            className="flex rounded-lg border border-border bg-muted/30 p-0.5 text-xs"
          >
            {DAILY_WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                aria-pressed={days === w}
                onClick={() => setDays(w)}
                className={cn(
                  "rounded-md px-2.5 py-1 font-medium transition-colors",
                  days === w
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {w}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-56 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error || !data ? (
        <p className="text-sm text-destructive">
          {getErrorMessage(error, "Failed to load the daily usage chart.")}
        </p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={data.series}
              margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                opacity={0.5}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
                tickFormatter={(v: string) => formatDay(v)}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
                allowDecimals={false}
                width={metric === "costMicroUsd" ? 72 : 50}
                tickFormatter={(v: number) => formatDailyValue(v, metric)}
              />
              <Tooltip
                labelFormatter={(label) => formatDay(String(label))}
                formatter={(value, _name, props) => {
                  const point = props.payload as (typeof data.series)[number];
                  const label =
                    DAILY_METRICS.find((m) => m.key === metric)?.label ??
                    metric;
                  if (point.date < data.since) return ["No data yet", label];
                  return [formatDailyValue(Number(value), metric), label];
                }}
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              />
              <Bar dataKey={metric} radius={[3, 3, 0, 0]}>
                {data.series.map((point) => (
                  <Cell
                    key={point.date}
                    fill={
                      point.date < data.since
                        ? "hsl(var(--muted-foreground) / 0.25)"
                        : "hsl(var(--primary))"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <p className="mt-2 text-xs text-muted-foreground">
            Muted bars are before {formatDay(data.since)}, when daily tracking
            began. Those days have no data recorded, not zero activity.
          </p>

          {/* Accessible fallback with the same numbers, always rendered —
              not just for screen readers: it is also how "no data" and "zero"
              stay distinguishable once bar height alone can't show it. */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <caption className="sr-only">
                Daily generations, credits and cost for the last {data.days}{" "}
                days. Days before {data.since} have no data recorded.
              </caption>
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th scope="col" className="py-1.5 pr-3 font-medium">
                    Date
                  </th>
                  <th
                    scope="col"
                    className="py-1.5 pr-3 text-right font-medium"
                  >
                    Generations
                  </th>
                  <th
                    scope="col"
                    className="py-1.5 pr-3 text-right font-medium"
                  >
                    Credits
                  </th>
                  <th scope="col" className="py-1.5 text-right font-medium">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {data.series.map((point) => {
                  const noData = point.date < data.since;
                  return (
                    <tr key={point.date}>
                      <td className="py-1.5 pr-3 tabular-nums">
                        {formatDay(point.date)}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {noData
                          ? "No data"
                          : point.generations.toLocaleString()}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">
                        {noData ? "No data" : point.credits.toLocaleString()}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">
                        {noData
                          ? "No data"
                          : formatMicroUsd(point.costMicroUsd)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export function AdminAIUsagePage() {
  const { data, isLoading, error } = useAdminAIUsage();
  const [ranking, setRanking] = useState<Ranking>("generations");

  if (isLoading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm text-destructive">
        Failed to load AI usage data.
      </div>
    );
  }

  // See COST_NOT_REPORTED. An API from before cost tracking also reported
  // every generation figure as zero, so there is nothing worth showing.
  if (!data.costThisMonth) {
    return (
      <div className="rounded-lg border border-border/50 bg-card p-4 text-center text-sm text-muted-foreground">
        {COST_NOT_REPORTED}
      </div>
    );
  }

  const month = formatPeriod(data.period);
  const since = formatPeriod(data.meteringStarted);
  const cost = data.costThisMonth;
  // Absent from an API older than platform cost, as well as when the sync has
  // not run; both mean the same thing to a reader.
  const platform = data.platformThisMonth as PlatformCost | null | undefined;
  const fullyLoaded: FullyLoadedCost = data.fullyLoaded ?? {
    activeUsers: 0,
    platformMicroUsd: 0,
    platformPerActiveUserMicroUsd: 0,
    perActiveUserMicroUsd: 0,
    method: "even-split-across-active-users",
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
          AI Usage & Cost
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          What users generated in {month} and what it cost us. Cost covers
          Gemini and X only; AWS hosting is not included yet.
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={TrendingUp}
          label={`Generations in ${month}`}
          value={data.totalGenerationsThisMonth}
          detail={
            data.totalCreditsThisMonth !== undefined
              ? `${data.totalCreditsThisMonth.toLocaleString()} credits · ${data.totalUsers.toLocaleString()} users`
              : `${data.totalUsers.toLocaleString()} users`
          }
        />
        <StatCard
          icon={Cpu}
          label={`Generations since ${since}`}
          value={data.totalGenerations}
          detail={
            data.totalCredits !== undefined
              ? `${data.totalCredits.toLocaleString()} credits`
              : undefined
          }
        />
        <StatCard
          icon={CurrencyDollar}
          label={`Cost in ${month}`}
          value={formatMicroUsd(cost.totalMicroUsd)}
          detail={`AI ${formatMicroUsd(cost.aiMicroUsd)} · X ${formatMicroUsd(cost.xMicroUsd)}`}
        />
        <StatCard
          icon={Receipt}
          label={`Cost since ${since}`}
          value={formatMicroUsd(data.costAllTimeMicroUsd)}
        />
        <StatCard
          icon={Cloud}
          label={`AWS in ${month}`}
          value={
            platform ? formatMicroUsd(platform.totalMicroUsd) : "Not recorded"
          }
          detail={
            platform
              ? `Through ${formatDay(platform.through)} · tagged resources only`
              : "The daily sync has not recorded it yet"
          }
        />
        <StatCard
          icon={Users}
          label="Fully loaded, per active user"
          value={formatMicroUsd(fullyLoaded.perActiveUserMicroUsd)}
          detail={`${fullyLoaded.activeUsers.toLocaleString()} active · ${formatMicroUsd(fullyLoaded.platformPerActiveUserMicroUsd)} of it AWS`}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        AWS cost covers resources carrying this app&apos;s tags, so it is a
        floor: Lambda&apos;s own log groups are not tagged. The per-user share
        is an allocation, not a measurement — AWS bills the environment, and
        nothing in that bill belongs to one user. Cloudflare, which serves the
        frontend, is not included.
      </p>

      <CostReliabilityNotice cost={cost} />

      <DailyUsageChart />

      {/* Absent from an API older than margin reporting. */}
      {data.revenueMicroUsd !== undefined &&
      data.marginMicroUsd !== undefined ? (
        <MarginCard
          month={month}
          revenueMicroUsd={data.revenueMicroUsd}
          costMicroUsd={cost.totalMicroUsd + fullyLoaded.platformMicroUsd}
          marginMicroUsd={data.marginMicroUsd}
          marginRatio={data.marginRatio ?? null}
          unpricedUsers={data.unpricedUsers ?? 0}
          platformRecorded={Boolean(platform)}
        />
      ) : (
        <p className="text-xs text-muted-foreground">
          This environment&apos;s API does not report margin yet. It will once
          the backend is deployed.
        </p>
      )}

      {data.priceChanges && <PriceChangeNotice report={data.priceChanges} />}

      {cost.unattributedMicroUsd > 0 && (
        <p className="text-xs text-muted-foreground">
          {formatMicroUsd(cost.unattributedMicroUsd)} of this month&apos;s cost
          came from work outside any user&apos;s request, such as scheduled
          jobs. It is in the totals above but not in any user&apos;s figure.
        </p>
      )}

      {/* Tier breakdown */}
      <div className="rounded-xl border border-border/50 bg-card p-5">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          By Tier ({month})
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(data.tierBreakdown).map(([tier, stats]) => (
            <div key={tier} className="rounded-lg border border-border/50 p-4">
              <span
                className={cn(
                  "inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
                  TIER_COLORS[tier] ?? TIER_COLORS.free
                )}
              >
                {tier}
              </span>
              <div className="mt-3 space-y-1 text-sm">
                <p>
                  <span className="font-semibold tabular-nums">
                    {stats.users.toLocaleString()}
                  </span>{" "}
                  <span className="text-muted-foreground">users</span>
                </p>
                <p>
                  <span className="font-semibold tabular-nums">
                    {stats.generations.toLocaleString()}
                  </span>{" "}
                  <span className="text-muted-foreground">
                    generations
                    {stats.credits !== undefined &&
                      ` (${stats.credits.toLocaleString()} credits)`}
                  </span>
                </p>
                <p>
                  <span className="font-semibold tabular-nums">
                    {formatMicroUsd(stats.costMicroUsd)}
                  </span>{" "}
                  <span className="text-muted-foreground">cost</span>
                </p>
                {stats.revenueMicroUsd !== undefined && (
                  <p>
                    <span className="font-semibold tabular-nums">
                      {formatMicroUsd(stats.revenueMicroUsd)}
                    </span>{" "}
                    <span className="text-muted-foreground">
                      revenue
                      {(stats.unpricedUsers ?? 0) > 0 &&
                        ` (${stats.unpricedUsers} unpriced)`}
                    </span>
                  </p>
                )}
                {stats.marginMicroUsd !== undefined && (
                  <p>
                    <MarginFigure
                      microUsd={stats.marginMicroUsd}
                      className="font-semibold tabular-nums"
                    />{" "}
                    <span className="text-muted-foreground">margin</span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.belowCost && <BelowCostList users={data.belowCost} />}

      {/* Top users */}
      <div className="rounded-xl border border-border/50 bg-card p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Top Users ({month})
          </h2>
          <div
            role="group"
            aria-label="Rank top users"
            className="flex w-fit rounded-lg border border-border bg-muted/30 p-0.5 text-xs"
          >
            {RANKINGS.map((r) => (
              <button
                key={r.key}
                type="button"
                aria-pressed={ranking === r.key}
                onClick={() => setRanking(r.key)}
                className={cn(
                  "rounded-md px-2.5 py-1 font-medium transition-colors",
                  ranking === r.key
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <TopUsersTable
          users={ranking === "cost" ? data.topCostUsers : data.topUsers}
        />
      </div>
    </div>
  );
}
